/**
 * AccountStatusToggle Component
 * Enables staff to toggle user account enabled/disabled status
 * 
 * Two modes:
 * 1. Disable Screen (isEnabled) - User can still log in but sees restricted screen
 * 2. Disable Login (loginDisabled) - User cannot log in at all (Cognito disabled)
 * 
 * Requirements: 19.2, 19.3, 19.6
 */

import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

// GraphQL Mutations for screen access (isEnabled)
export const DISABLE_USER = gql`
  mutation DisableUser($userId: ID!) {
    disableUser(userId: $userId) {
      id
      isEnabled
      loginDisabled
    }
  }
`;

export const ENABLE_USER = gql`
  mutation EnableUser($userId: ID!) {
    enableUser(userId: $userId) {
      id
      isEnabled
      loginDisabled
    }
  }
`;

// GraphQL Mutations for login access (loginDisabled)
export const DISABLE_USER_LOGIN = gql`
  mutation DisableUserLogin($userId: ID!) {
    disableUserLogin(userId: $userId) {
      id
      isEnabled
      loginDisabled
    }
  }
`;

export const ENABLE_USER_LOGIN = gql`
  mutation EnableUserLogin($userId: ID!) {
    enableUserLogin(userId: $userId) {
      id
      isEnabled
      loginDisabled
    }
  }
`;

interface AccountStatusToggleProps {
  /** User ID to toggle status for */
  userId: string;
  /** Current enabled status (screen access) */
  isEnabled: boolean;
  /** Current login disabled status */
  loginDisabled?: boolean;
  /** User's display name for confirmation dialog */
  userName?: string;
  /** Callback after status changes */
  onStatusChange?: (isEnabled: boolean, loginDisabled: boolean) => void;
  /** Show as compact inline or full card view */
  compact?: boolean;
}

export function AccountStatusToggle({
  userId,
  isEnabled,
  loginDisabled = false,
  userName = 'this user',
  onStatusChange,
  compact = false,
}: AccountStatusToggleProps) {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [dialogAction, setDialogAction] = useState<'disableScreen' | 'disableLogin' | null>(null);
  const [processing, setProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [disableUser] = useMutation(DISABLE_USER);
  const [enableUser] = useMutation(ENABLE_USER);
  const [disableUserLogin] = useMutation(DISABLE_USER_LOGIN);
  const [enableUserLogin] = useMutation(ENABLE_USER_LOGIN);

  const handleDisableScreen = () => {
    if (isEnabled) {
      setDialogAction('disableScreen');
      setShowConfirmDialog(true);
    } else {
      performEnableScreen();
    }
  };

  const handleDisableLogin = () => {
    if (!loginDisabled) {
      setDialogAction('disableLogin');
      setShowConfirmDialog(true);
    } else {
      performEnableLogin();
    }
  };

  const performEnableScreen = async () => {
    setProcessing(true);
    setSuccessMessage(null);
    try {
      await enableUser({ variables: { userId } });
      setSuccessMessage(`Account screen access restored for ${userName}.`);
      onStatusChange?.(true, loginDisabled);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      console.error('Error enabling user:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const performDisableScreen = async () => {
    setProcessing(true);
    setSuccessMessage(null);
    setShowConfirmDialog(false);
    try {
      await disableUser({ variables: { userId } });
      setSuccessMessage(`Account disabled for ${userName}. They will see a restricted screen.`);
      onStatusChange?.(false, loginDisabled);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      console.error('Error disabling user:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const performEnableLogin = async () => {
    setProcessing(true);
    setSuccessMessage(null);
    try {
      await enableUserLogin({ variables: { userId } });
      setSuccessMessage(`Login access restored for ${userName}.`);
      onStatusChange?.(isEnabled, false);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      console.error('Error enabling user login:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const performDisableLogin = async () => {
    setProcessing(true);
    setSuccessMessage(null);
    setShowConfirmDialog(false);
    try {
      await disableUserLogin({ variables: { userId } });
      setSuccessMessage(`Login blocked for ${userName}. They cannot sign in.`);
      onStatusChange?.(isEnabled, true);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      console.error('Error disabling user login:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirm = () => {
    if (dialogAction === 'disableScreen') {
      performDisableScreen();
    } else if (dialogAction === 'disableLogin') {
      performDisableLogin();
    }
  };

  const handleCancel = () => {
    setShowConfirmDialog(false);
    setDialogAction(null);
  };

  // Get overall status for display
  const getStatusBadge = () => {
    if (loginDisabled) {
      return <Badge variant="default" className="bg-red-600 text-white">Login Blocked</Badge>;
    }
    if (!isEnabled) {
      return <Badge variant="default" className="bg-yellow-100 text-yellow-700">Restricted</Badge>;
    }
    return <Badge variant="success">Active</Badge>;
  };

  if (compact) {
    return (
      <div className="flex items-center gap-3 flex-wrap">
        {getStatusBadge()}

        <Button
          variant="outline"
          size="sm"
          onClick={handleDisableScreen}
          disabled={processing}
        >
          {processing ? 'Processing...' : isEnabled ? 'Restrict Access' : 'Restore Access'}
        </Button>

        <Button
          variant={loginDisabled ? 'default' : 'outline'}
          size="sm"
          onClick={handleDisableLogin}
          disabled={processing}
          className={loginDisabled ? 'bg-green-600 hover:bg-green-700 text-white' : ''}
        >
          {processing ? 'Processing...' : loginDisabled ? 'Enable Login' : 'Block Login'}
        </Button>

        {successMessage && (
          <span className="text-sm text-green-600">{successMessage}</span>
        )}

        <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {dialogAction === 'disableScreen' ? 'Restrict Account Access' : 'Block User Login'}
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {dialogAction === 'disableScreen' ? (
                <>
                  <p className="text-gray-700 mb-4">
                    Are you sure you want to restrict access for <strong>{userName}</strong>?
                  </p>
                  <p className="text-sm text-gray-500 mb-6">
                    The user will still be able to log in but will see a restricted screen with read-only access.
                    They will not be able to send messages or upload documents.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-gray-700 mb-4">
                    Are you sure you want to block login for <strong>{userName}</strong>?
                  </p>
                  <p className="text-sm text-gray-500 mb-6">
                    The user will NOT be able to sign in at all. If they are currently logged in,
                    they will be signed out automatically on their next action.
                  </p>
                </>
              )}
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleConfirm}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {dialogAction === 'disableScreen' ? 'Restrict Access' : 'Block Login'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-medium text-gray-900">Account Status</h3>
          <p className="text-xs text-gray-500 mt-1">
            {loginDisabled 
              ? 'This account is blocked from logging in.'
              : !isEnabled 
                ? 'This account has restricted access (read-only).'
                : 'This account is active with full platform access.'}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <div 
            className={`w-3 h-3 rounded-full ${
              loginDisabled ? 'bg-red-600' : !isEnabled ? 'bg-yellow-500' : 'bg-green-500'
            }`}
            aria-hidden="true"
          />
          {getStatusBadge()}
        </div>
      </div>

      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
          <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-green-800 text-sm">{successMessage}</span>
        </div>
      )}

      <div className="space-y-3">
        {/* Screen Access Toggle */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div>
            <p className="text-sm font-medium text-gray-700">Screen Access</p>
            <p className="text-xs text-gray-500">
              {isEnabled ? 'User has full access' : 'User sees restricted screen'}
            </p>
          </div>
          <Button
            variant={isEnabled ? 'outline' : 'default'}
            size="sm"
            onClick={handleDisableScreen}
            disabled={processing}
            className={!isEnabled ? 'bg-green-600 hover:bg-green-700 text-white' : ''}
          >
            {processing ? 'Processing...' : isEnabled ? 'Restrict' : 'Restore'}
          </Button>
        </div>

        {/* Login Access Toggle */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div>
            <p className="text-sm font-medium text-gray-700">Login Access</p>
            <p className="text-xs text-gray-500">
              {loginDisabled ? 'User cannot sign in' : 'User can sign in'}
            </p>
          </div>
          <Button
            variant={loginDisabled ? 'default' : 'outline'}
            size="sm"
            onClick={handleDisableLogin}
            disabled={processing}
            className={loginDisabled ? 'bg-green-600 hover:bg-green-700 text-white' : 'border-red-300 text-red-600 hover:bg-red-50'}
          >
            {processing ? 'Processing...' : loginDisabled ? 'Enable' : 'Block'}
          </Button>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialogAction === 'disableScreen' ? 'Restrict Account Access' : 'Block User Login'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center gap-3 mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <svg className="w-6 h-6 text-yellow-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-yellow-800 text-sm">
                You are about to {dialogAction === 'disableScreen' ? 'restrict access' : 'block login'} for <strong>{userName}</strong>.
              </p>
            </div>
            
            {dialogAction === 'disableScreen' ? (
              <>
                <p className="text-gray-700 mb-4">
                  This will restrict the user's access:
                </p>
                <ul className="text-sm text-gray-600 mb-6 space-y-2">
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Can still log in to the platform
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Can view their information (read-only)
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Cannot send messages
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Cannot upload documents
                  </li>
                </ul>
              </>
            ) : (
              <>
                <p className="text-gray-700 mb-4">
                  This will completely block the user from signing in:
                </p>
                <ul className="text-sm text-gray-600 mb-6 space-y-2">
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Cannot log in to the platform at all
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    If currently logged in, will be signed out
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" />
                    </svg>
                    Will see "Account disabled" error on sign in attempt
                  </li>
                </ul>
              </>
            )}
            
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleConfirm}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {dialogAction === 'disableScreen' ? 'Restrict Access' : 'Block Login'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AccountStatusToggle;
